import {
  Alert,
  Box,
  Heading,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  MenuButton,
  MenuList,
  MenuItem,
  Menu,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  CreditFacilityGQLType,
  LoanProductGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { UpdateImplementationNameModal } from '../UpdateImplementationNameModal';
import { useImplementationDetailsQuery } from './implementationDetails.gql.generated';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';

// Helpers
type LoanProductsTableProps = {
  loanProducts: Pick<LoanProductGQLType, 'id' | 'name' | 'slug'>[];
};

const LoanProductsTable = ({ loanProducts }: LoanProductsTableProps) => {
  return (
    <Box mb="36px">
      <Heading size="md" mb="24px">
        Loan Products
      </Heading>
      <Table>
        <Thead>
          <Tr>
            <Th>Id</Th>
            <Th>Name</Th>
            <Th>Slug</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loanProducts.map((loanProduct) => (
            <Tr key={loanProduct.id}>
              <Td>{loanProduct.id}</Td>
              <Td>{loanProduct.name}</Td>
              <Td>{loanProduct.slug}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {loanProducts.length === 0 && (
        <Alert> No loan products are defined.</Alert>
      )}
    </Box>
  );
};

type CreditFacilityTableProps = {
  creditFacilities: Pick<
    CreditFacilityGQLType,
    'id' | 'name' | 'borrowerOrganizationName'
  >[];
};
const CreditFacilitiesTable = ({
  creditFacilities,
}: CreditFacilityTableProps) => {
  return (
    <Box mb="36px">
      <Heading size="md" mb="24px">
        Credit Facilities
      </Heading>
      <Table>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Borrower</Th>
          </Tr>
        </Thead>
        <Tbody>
          {creditFacilities.map((creditFacility) => (
            <Tr key={creditFacility.id}>
              <Td color={colors.blue[400]}>
                <Link to={`facilities/${creditFacility.id}`}>
                  {creditFacility.name}
                </Link>
              </Td>
              <Td>{creditFacility.borrowerOrganizationName}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {creditFacilities.length === 0 && (
        <Alert> No credit facilities are defined.</Alert>
      )}
    </Box>
  );
};

// Our usual concept of "loan product" doesn't apply for SLS facilities, but the
// entity still exists in the DB in order to satisfy certain validations. No
// need to show these in the UI.
const HIDE_LOAN_PRODUCTS_FOR_ORG_SLUGS = ['valleyBankSls', 'alleyBankSls'];

// Exports
export const ImplementationDetails = ({
  implementationId,
}: {
  implementationId: string;
}) => {
  const menuControls = useDisclosure();
  const updateImplementationNameControls = useDisclosure();

  const { user } = useUserAuthOrThrow();

  const query = useImplementationDetailsQuery({
    variables: { implementationId },
  });
  if (query.error) throw query.error;
  if (!query.data || query.loading) return <Skeleton h="600px" />;

  const implementation = query.data.implementation;

  return (
    <>
      <Flex direction="row" justifyContent="space-between">
        <Heading size="lg" mb="24px">
          {implementation.name}
        </Heading>
        <Menu {...menuControls} computePositionOnMount strategy="fixed">
          <MenuButton
            as={IconButtonV2}
            variant="secondary-muted"
            colorScheme="gray"
            isActive={menuControls.isOpen}
            aria-label="Options"
            icon={<Icon icon="ellipsis-vertical" />}
            tooltipEnabled={false}
          />
          <MenuList zIndex="dropdown">
            <MenuItem onClick={updateImplementationNameControls.onOpen}>
              <Icon icon="edit" style={{ marginRight: '0.75rem' }} />
              Edit Implementation Name
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      {HIDE_LOAN_PRODUCTS_FOR_ORG_SLUGS.includes(
        user.organization.slug,
      ) ? null : (
        <LoanProductsTable loanProducts={implementation.loanProducts} />
      )}
      <CreditFacilitiesTable
        creditFacilities={implementation.creditFacilities}
      />
      <UpdateImplementationNameModal
        modalControls={updateImplementationNameControls}
        currentName={implementation.name}
        implementationId={implementation.id}
      />
    </>
  );
};
